import { useEffect, useState } from 'react';
import { UseControllerProps, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Stack, Textarea, TextInput } from '@mantine/core';
import { z } from 'zod';

import { CreateIcon, TrashIcon } from 'icons';

type Props = UseControllerProps & {
  mode: 'multiple_inputs' | 'textarea';
};

export const EmailsListField = ({ mode, name }: Props) => {
  const { setValue, watch } = useFormContext();
  const value = watch(name);
  if (!Array.isArray(value)) throw new Error('Value must be an array');

  const [length, setLength] = useState(value.length);
  const [textareaValue, setTextareaValue] = useState(value.join(', '));
  const allFieldsCorrect = value.every(
    email => z.string().email().safeParse(email).success,
  );

  const { t } = useTranslation('common');

  const onSingleFieldChange = (fieldIndex: number, fieldValue: string) => {
    setValue(`${name}.${fieldIndex}`, fieldValue);
    setTextareaValue(tV => {
      const tVArray = tV.split(',');
      tVArray[fieldIndex] = fieldValue;
      return tVArray.map(v => v.trim()).join(', ');
    });
  };

  const removeField = (index: number) => {
    setValue(
      name,
      value.filter((_, i) => i !== index),
    );
    setLength(l => l - 1);
  };

  const parseTextareaValue = (v: string) => {
    return v.split(',').map(email => email.trim());
  };

  useEffect(() => {
    setLength(value.length);
    setTextareaValue(value.join(', '));
  }, [value.length]);

  return mode === 'multiple_inputs' ? (
    <>
      <Stack spacing="xs">
        {Array.from({ length }, (_, i) => `email-list-field-${i}`).map(
          (key, i) => (
            <TextInput
              key={key}
              value={watch(`${name}.${i}`)}
              onChange={e => onSingleFieldChange(i, e.target.value)}
              placeholder={t('emailsListField.multipleInputs.placeholder')}
              rightSection={
                length > 1 && (
                  <TrashIcon
                    size={20}
                    color="red"
                    onClick={() => removeField(i)}
                  />
                )
              }
            />
          ),
        )}
      </Stack>
      <Button
        onClick={() => setValue(name, [...value, ''])}
        variant="subtle"
        color="green"
        leftIcon={<CreateIcon size={20} />}
        disabled={!allFieldsCorrect}
      >
        {t('emailsListField.multipleInputs.addButton')}
      </Button>
    </>
  ) : (
    <Textarea
      minRows={4}
      value={textareaValue}
      onChange={e => setTextareaValue(e.target.value)}
      onBlur={() => setValue(name, parseTextareaValue(textareaValue))}
      placeholder={t('emailsListField.textarea.placeholder')}
    />
  );
};
